import {sendDevConsoleVehicleProps, sendDevConsoleVehiclePropsRequest} from '../../vuplex';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Switch,
} from '@chakra-ui/react';
import React, {useEffect, useState} from 'react';
import {useVuplex} from '../../hooks/useVuplex';
import _ from 'lodash';
import {NumberControl, SelectControl, StringControl} from './NumberControl';


const getControl = (name, value, onChange) => {
  const isMomentarySwitch = typeof value === 'boolean' && name.endsWith('MomentarySwitch');
  if (isMomentarySwitch) {
    return (
      <Button onClick={() => onChange(true)}>
        {name.replace(/MomentarySwitch$/, '')}
      </Button>
    );
  }

  const controlByName = {
    dspBufferSize: (
      <SelectControl
        name={name}
        value={value}
        onChange={onChange}
        options={[
          {label: '256', value: 256},
          {label: '512', value: 512},
          {label: '1024', value: 1024},
        ]}
      />
    ),
    fixedTimestep: (
      <NumberControl
        name={name}
        value={value}
        onChange={onChange}
        step={.001}
        precision={4}
      />
    ),
    jetpackThrust: (
      <NumberControl
        name={name}
        value={value}
        onChange={onChange}
        step={100}
      />
    ),
    jetpackTiltDamping: (
      <NumberControl
        name={name}
        value={value}
        onChange={onChange}
        step={1}
      />
    ),
    jetpackTiltDampGrounded: (
      <NumberControl
        name={name}
        value={value}
        onChange={onChange}
        step={1}
      />
    ),
    fogDensity: (
      <NumberControl
        name={name}
        value={value}
        onChange={onChange}
        step={0.01}
      />
    )
  };

  const controlByType = {
    string: (
      <StringControl
        name={name}
        value={value}
        onChange={onChange}
      />
    ),
    number: (
      <NumberControl
        name={name}
        value={value}
        onChange={onChange}
      />
    ),
    boolean: (
      <Switch
        isChecked={value}
        onChange={(e) => onChange(e.target.checked)}
      />
    ),
  };

  return controlByName[name] || controlByType[typeof value];
};

const sendPropsDebounced = _.debounce((props) => {
  sendDevConsoleVehicleProps(props);
}, 200);

const sort = (obj) => _.pick(obj, Object.keys(obj).sort());

const VehicleProps = () => {
  const [vehicleProps, setVehicleProps] = useState({});
  const [searchText, setSearchText] = useState('');

  useVuplex({
    vehicleProps: (props) => {
      setVehicleProps(props);
      // console.log('vehicleProps', props);
    },
  });

  useEffect(() => {
    sendDevConsoleVehiclePropsRequest();
  }, []);

  const {CategoryNameToPropNames = []} = vehicleProps;

  const allCategoryKeys = _.flatMap(CategoryNameToPropNames, ({presets = [], prefix}) => {
    const prefixKeys = _.keys(_.pickBy(vehicleProps, (v, k) => k.startsWith(prefix)));
    return [...presets, ...prefixKeys];
  });

  const generalProps = _.pickBy(vehicleProps, (v, k) => !_.includes([
    'CategoryNameToPropNames',
    ...allCategoryKeys,
  ], k));

  const normalizedSearchText = _.trim(searchText).toLowerCase();
  const isSearching = normalizedSearchText.length > 0;
  const searchableVehicleProps = _.omit(vehicleProps, ['CategoryNameToPropNames']);

  const onPropChange = (name) => (nextVal) => {
    const next = {
      ...vehicleProps,
      [name]: nextVal,
    };

    setVehicleProps(next);
    sendPropsDebounced(_.omit(next, ['CategoryNameToPropNames']));
  };

  const filteredVehicleProps = _.pickBy(searchableVehicleProps, (value, name) => (
    name.toLowerCase().includes(normalizedSearchText) && !!getControl(name, value, onPropChange(name))
  ));

  return (<>

    <Flex justify="space-around" w="full" p={2} gap={2}>
      <Flex gap={2} flex={1} alignItems="center">
        <Button
          onClick={sendDevConsoleVehiclePropsRequest}
        >
          Refresh
        </Button>
        <Input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search..."
          maxW="200px"
        />
      </Flex>

      <Switch
        isChecked={vehicleProps.vehicleEnabled}
        onChange={(e) => {
          onPropChange('vehicleEnabled')(e.target.checked);
        }}
        size="lg"
        alignContent="center"
      >
        Vehicle Enabled
      </Switch>
    </Flex>


    {isSearching ? (
      <SimpleGrid columns={3} spacing={2}>
        {_.map(sort(filteredVehicleProps), (value, name) => (
          <FormControl key={name} border="1px solid" borderRadius="2xl" p={2}>
            <FormLabel>{name}</FormLabel>
            {getControl(name, value, onPropChange(name))}
          </FormControl>
        ))}
      </SimpleGrid>
    ) : (
      <Accordion allowToggle>
        <AccordionItem>
          <AccordionButton>
            <Box as="span" flex="1" textAlign="left">
              <Heading size="md">General</Heading>
            </Box>
            <AccordionIcon/>
          </AccordionButton>
          <AccordionPanel pb={4}>
            <SimpleGrid columns={3} spacing={2}>
              {_.map(sort(generalProps), (value, name) => (
                <FormControl key={name} border="1px solid" borderRadius="2xl" p={2}>
                  <FormLabel>{name}</FormLabel>
                  {getControl(name, value, onPropChange(name))}
                </FormControl>
              ))}
            </SimpleGrid>

          </AccordionPanel>
        </AccordionItem>

        {CategoryNameToPropNames.map(({category, presets = [], prefix}) => (
          <AccordionItem key={category}>
            <AccordionButton>
              <Box as="span" flex="1" textAlign="left">
                <Heading size="md">{category}</Heading>
              </Box>
              <AccordionIcon/>
            </AccordionButton>
            <AccordionPanel pb={4}>
              <SimpleGrid columns={3} spacing={2}>
                {_.map(sort(_.pickBy(vehicleProps, (v, k) => k.startsWith(prefix) || _.includes(presets, k) )), (value, name) => (
                  <FormControl key={name} border="1px solid" borderRadius="2xl" p={2}>
                    <FormLabel>{_.replace(name, prefix, '')}</FormLabel>
                    {getControl(name, value, onPropChange(name))}
                  </FormControl>
                ))}
              </SimpleGrid>

            </AccordionPanel>
          </AccordionItem>
        ))}

      </Accordion>
    )}

  </>);
};

export default VehicleProps;

